import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { createStyles, Divider, Theme, withStyles, WithStyles } from '@material-ui/core';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import {
  Domain as AddressIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Videocam as VideoIcon,
} from '@material-ui/icons';
import classnames from 'classnames';
import { get, isEmpty, map } from 'lodash';
import moment from 'moment';
import { TAppointment } from 'ui-model';

import { Button, Typography, TypographyWithIcon } from 'components/v2';

import { ContentLoading, EmptyState, RescheduleButton } from './components';

const messages = defineMessages({
  onlineAppt: {
    id: 'modules.LumaBot.components.ManageAppointments.components.ListAppointments.onlineAppt',
    defaultMessage: 'Online appointment',
  },
});

const styles = ({ spacing: { unit }, palette }: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: `${unit}px`,
      margin: `${unit * 1.5}px 0`,
    },
    button: {
      display: 'flex',
      backgroundColor: palette.background.default,
      border: '1px solid transparent',
      width: '100%',
      minHeight: 56,
      padding: `${unit}px ${unit * 1.5}px`,
      cursor: 'pointer',
      boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)',
      '&:hover': {
        backgroundColor: lighten(palette.primary.main, 0.9),
      },
      '& span': {
        display: 'block ',
      },
    },
    selectedButton: {
      backgroundColor: lighten(palette.primary.main, 0.9),
      borderColor: palette.primary.main,
      height: 'fit-content',
    },
    icon: {
      color: 'inherit',
      fontSize: '20px',
    },
    btnContent: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
    },
    btnContentExpanded: {
      height: 0,
      opacity: 0,
    },
    btnContentExpandedVisible: {
      height: 'fit-content',
      opacity: 1,
      marginTop: unit,
      width: '100%',
    },
    typography: {
      color: 'inherit',
      display: 'flex',
      marginBottom: unit / 2,
      maxWidth: '200px',
    },
    divider: {
      marginBottom: unit,
      height: 2,
    },
  });

export interface ListAppointmentsProps extends WithStyles<typeof styles> {
  appointments: TAppointment[];
  onSubmit: (appointment: TAppointment) => void;
  handleAppointmentSelect: (appointment: TAppointment) => void;
  selectedAppointment?: TAppointment;
  isLoading?: boolean;
  isDisabled: boolean;
  isRescheduleLoading?: boolean;
}

const ListAppointments: React.FC<ListAppointmentsProps> = ({
  classes,
  appointments,
  selectedAppointment,
  handleAppointmentSelect,
  isLoading,
  isDisabled,
  onSubmit,
  isRescheduleLoading,
}) => {
  const { formatMessage } = useIntl();

  if (isLoading) return <ContentLoading classRoot={classes.root} classButton={classes.button} />;

  if (isEmpty(appointments)) return <EmptyState classRoot={classes.root} />;

  return (
    <div className={classes.root}>
      {map(appointments, (appointment) => {
        const { _id: id } = appointment;
        const isAppointmentSelected = id === get(selectedAppointment, '_id');
        const isTelehealth = get(appointment, 'type.modality') === 'telehealth';
        const appointmentDate = moment(appointment.date);
        const formattedDate = isAppointmentSelected
          ? appointmentDate.format('[On] MM/DD [at] h:mm A')
          : appointmentDate.format('MM/DD h:mm A');
        const facilityPhoneNumber = get(appointment, 'facility.phone', '');
        const formattedFacilityPhoneNumber = facilityPhoneNumber.replace(/\+1 /, '');

        return (
          <Button
            data-lh-id={`btn-appointment-${id}`}
            key={id}
            className={classnames(classes.button, { [classes.selectedButton]: isAppointmentSelected && !isDisabled })}
            onClick={() => handleAppointmentSelect(appointment)}
            extraRounded
            disabled={isDisabled}
          >
            <div className={classes.btnContent}>
              <Typography variant="bW600F14" noWrap className={classes.typography}>
                {get(appointment, 'type.name', '...')}
              </Typography>
              <Typography variant="bW600F14" noWrap className={classes.typography}>
                {formattedDate}
              </Typography>

              <div
                className={classnames(classes.btnContentExpanded, {
                  [classes.btnContentExpandedVisible]: isAppointmentSelected,
                })}
              >
                <Divider variant="fullWidth" className={classes.divider} />
                {!isTelehealth && (
                  <>
                    <TypographyWithIcon
                      icon={<AddressIcon className={classes.icon} />}
                      variant="bW600F14"
                      noWrap
                      className={classes.typography}
                    >
                      <Typography variant="bW600F14" noWrap className={classes.typography}>
                        {get(appointment, 'facility.name', '...')}
                      </Typography>
                      <Typography variant="bW600F14" noWrap className={classes.typography}>
                        {get(appointment, 'facility.address', '...')}
                      </Typography>
                    </TypographyWithIcon>
                    <TypographyWithIcon
                      icon={<PhoneIcon className={classes.icon} />}
                      variant="bW600F14"
                      noWrap
                      className={classes.typography}
                    >
                      {formattedFacilityPhoneNumber}
                    </TypographyWithIcon>
                    <TypographyWithIcon
                      icon={<PersonIcon className={classes.icon} />}
                      variant="bW600F14"
                      noWrap
                      className={classes.typography}
                    >
                      {get(appointment, 'provider.name', '')}
                    </TypographyWithIcon>
                  </>
                )}
                {isTelehealth && (
                  <TypographyWithIcon
                    icon={<VideoIcon className={classes.icon} />}
                    variant="bW600F14"
                    noWrap
                    className={classes.typography}
                  >
                    {formatMessage(messages.onlineAppt)}
                  </TypographyWithIcon>
                )}

                <RescheduleButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onSubmit(appointment);
                  }}
                  disabled={isDisabled}
                  isLoading={isRescheduleLoading}
                />
              </div>
            </div>
          </Button>
        );
      })}
    </div>
  );
};

export default withStyles(styles, { name: 'ListAppointments' })(ListAppointments);
